import React, { useEffect, useRef, useState } from 'react';
import { Network } from 'vis-network';
import './GraphVisualizer.css'

const GraphVisualizer = () => {
  const containerRef = useRef(null);
  const [network, setNetwork] = useState(null);
  const [userNodes, setUserNodes] = useState("")
  const [userEdges, setUserEdges] = useState([])
  const [colors, setColors] = useState({})

  // animation, store previous node color
  const [colorHistory, setColorHistory] = useState([])
  const [highlightEdges, setHighlightedEdges] = useState([])
  const [currentIdx, setCurrentIdx] = useState(0)

  // Process user's input: nodes
  const handleChange = (event)=> {
    setUserNodes(event.target.value) // update value on change
  }

  // Process user's input: edges
  const handleEdges = (event)=> {
    const inputValues = event.target.value; 
    // use regex to extract tuples
    const regex = /\((\d+),\s*(\d+)\)/g;
    const values = [...inputValues.matchAll(regex)];
    const parsedValues = values.map(
      value => [parseInt(value[1]), parseInt(value[2])]
    )
    setUserEdges(parsedValues);
  };

  // initialize network with Vis.js
  const initializeNetwork = (event) => {
    event.preventDefault(); // Prevent page reload

    // check user's input 
    if (!userNodes || userEdges.length === 0) {
      console.error("Enter valid nodes and edges before initializing.");
      return;
    }

    const nodes = [];
    for (let i=1; i <= Number(userNodes); i++){
      nodes.push({id:i, label:`${i}`})
    }
    
    const edges = userEdges.map(([val1, val2]) => ({ 
      id: `edge-${val1}-${val2}`, // Assign a unique edge ID
      from: val1, 
      to: val2,
     }));

    const data = { nodes, edges };
    const options = { nodes: {
        shape: 'circle',
        size: 60,
        font: { size: 14, color: 'black' },
        color: 'white'
      },
      edges: {
        color: 'orange'
      },
      physics: {
        enabled: true
      }
    };

    // Initialize and display the network 
    if (containerRef.current) {
      const networkInstance = new Network(containerRef.current, data, options);
      setNetwork(networkInstance); 
    }
  };

  const URL = process.env.REACT_APP_API_URL


  // Make a POST request to the API to compute graph coloring. Backend returns an array of colors 
    const solve_graph_coloring = async(event) => { 
      event.preventDefault(); // Prevent page reload

      if (!userNodes || userEdges.length === 0) {
        console.error("Enter valid nodes and edges before solving.");
        return;
      }
      //check data is being sent to the backend 
      console.log("Sending data:", { param1: Number(userNodes), param2: userEdges });

      const response = await fetch(URL, {
            method: 'POST', 
            headers: {'Content-Type': 'application/json'},  
            body: JSON.stringify({
              param1: Number(userNodes), // Number of nodes
              param2: userEdges, //list of edges as tuples 
            }),  
          });

      const data = await response.json();
      const availableColors = {}
      
      for (const [idx, e] of data['message'].entries()) {
        availableColors[idx+1] = e
      }

      setColors(availableColors);
      setCurrentIdx(0);
      setColorHistory([]);
      
    };

    // Function to apply color one node at a time 
    const applyColorByNode = (event) => {      
      event.preventDefault(); // Prevent page reload

      if (currentIdx < Object.keys(colors).length) {
        const nodeId = Object.keys(colors)[currentIdx];

        setColorHistory((prev) => [...prev, { nodeId, color: colors[nodeId] }]);
        setColors((prevColors) => {
          const newColors = {...prevColors, [nodeId]: colors[nodeId]};
          // Update node color
          network.body.data.nodes.update({ id: parseInt(nodeId), color: colors[nodeId]});


          // Highlight edges connected to this node
          const edgesArray = network.body.data.edges.get(); // get edges from network
          const activeEdges = edgesArray.filter(edge => edge.from === parseInt(nodeId));

          network.body.data.edges.update(
            activeEdges.map(edge => ({
              id: `edge-${edge.from}-${edge.to}`,
              color: { color: "yellow" } // Change the color
            }))
          );

          setHighlightedEdges(activeEdges);
          return newColors;
        });

        setCurrentIdx((prev) => prev + 1);
        console.log('cureenttt idx', currentIdx)
      }
    };


    // Function to revert back to plast modified node
    const previousColorFunction = (event) => {  
      event.preventDefault(); // Prevent page reload

      if (colorHistory.length > 0) {
        // get last modified node
        const lastNodeChange = colorHistory[colorHistory.length-1]        
        setColorHistory((prev) => prev.slice(0, -1)); //remove last entry

        setColors((prevColors) => {
          const newColors = { ...prevColors, [parseInt(lastNodeChange.nodeId)]: lastNodeChange.color };
    
          network.body.data.nodes.update({
            id: parseInt(lastNodeChange.nodeId),
            color: 'white' // change node color
          });

          
          // Reset edge colors
          const edgesArray = network.body.data.edges.get();
          const activeEdges = edgesArray.filter(edge => edge.from === parseInt(lastNodeChange.nodeId));

          network.body.data.edges.update(
            activeEdges.map(edge => ({
              id: `edge-${edge.from}-${edge.to}`,
              color: { color: "white" } // Change color
            }))
          );

          setHighlightedEdges([]);
    
          return newColors;
        });
    
        setCurrentIdx((prev) => prev - 1); // Decrease index safely
      } else {
        console.log("No previous state to revert to.");
      }
      };


    // Solve 3-coloring graph: Make POST request and compute Step-by-Step Visualization
    const solve_graph_coloring_STEP = async(event) => { 
      event.preventDefault(); // Prevent page reload

      if (!userNodes || userEdges.length === 0) {
        console.error("Enter valid nodes and edges before solving.");
        return;
      }

      console.log("Sending data:", { param1: Number(userNodes), param2: userEdges });

      const response = await fetch(URL, {
            method: 'POST', 
            headers: {'Content-Type': 'application/json'},  
            body: JSON.stringify({
              param1: Number(userNodes), // Number of nodes
              param2: userEdges, //list of edges as tuples 
            }),  
          });

      const data = await response.json();
      const availableColors = {}
      
      for (const [idx, e] of data['message'].entries()) {
        availableColors[idx+1] = e
      }

      const edgesArray = network.body.data.edges.get();
      graphColoringStepByStep(availableColors, edgesArray);
    };

    // Function to apply graph coloring step-by-step using setTimeOut
    const graphColoringStepByStep = (nodeColors, edgesArr) => {
      if (!network) return;
      const nodeIds = Object.keys(nodeColors);

      nodeIds.forEach((nodeId, index) => {
        setTimeout(() => {
          const newColors = { ...colors, [nodeId]: nodeColors[nodeId] };

          // Update node colors in the graph
          network.body.data.nodes.update(
            Object.keys(newColors).map((id) => ({
              id: parseInt(id),
              color: newColors[id],
            }))
          );

          setColors(newColors); // Set the updated colors state

          const activeEdges = edgesArr.filter(edge => edge.from === parseInt(nodeId));

          network.body.data.edges.update(
            activeEdges.map(edge => ({
              id: `edge-${edge.from}-${edge.to}`,
              color: { color: "yellow" } // Change color
            }))
          );

          setHighlightedEdges(activeEdges);
        }, index * 800); 
      });
    };


    // Make POST request, get results and compute visualization with no animation 
  const graphColoring = async(event) => { 
    event.preventDefault(); // Prevent page reload

    if (!userNodes || userEdges.length === 0) {
      console.error("Enter valid nodes and edges before solving.");
      return;
    }

    console.log("Sending data:", { param1: Number(userNodes), param2: userEdges });

    const response = await fetch(URL, {
          method: 'POST', 
          headers: {'Content-Type': 'application/json'},  
          body: JSON.stringify({
            param1: Number(userNodes), // Number of nodes
            param2: userEdges, //list of edges as tuples 
          }),  
        });

    const data = await response.json();
    const availableColors = {}
    
    for (const [idx, e] of data['message'].entries()) {
      availableColors[idx+1] = e
    }

    setColors(availableColors);

    
    if (!network) return;
  

    // Update node colors 
    network.body.data.nodes.update(
      Object.keys(availableColors).map((id) => ({ id: parseInt(id), color: availableColors[id]}))
    );
  };
  

  return (
    <div className='algo-container'>
      <div className='algo-description'>
        <h1 className='algo-title'> 3-Coloring Graph </h1> 
        <p>Color the nodes of a graph with 3 colors such that no two adjacent nodes have the same color.</p>
        
        <div className='algo-steps'>
        <p>
         For step-by-step visualization:<br/>
          1. Input number of nodes and edge connections <br/>   
          2. Initialize Graph <br/>
          3. Start Visualization <br/>
          4. Use 'Previous' and 'Next' buttons <br/>
        </p>

        <p>
          For complete graph visualization:<br/>
          1. Initialize Graph or Reset <br/>
          2. Visualize Execution
        
        </p>
        </div>
        
      </div>

      <div className='algo-vis' ref={containerRef} style={{ width: '1000px', height: '500px' }}>
      
      </div>
              
              
      <div className="bottom-section">
        <section>
      
          <form>
              <input 
                type="number" 
                placeholder='Number of nodes: 4'
                value={userNodes}  
                onChange={handleChange}  
              />
              <input 
                type="text" 
                placeholder='Edge list: (1, 2), (2, 3), (3, 4), (4,1)'
                onChange={handleEdges}  
              />
  
        <div className='bttn-container'>
              <button onClick={initializeNetwork}> Initialize Graph </button>
              <button onClick={solve_graph_coloring}>Start Visualization</button>
              <button onClick={previousColorFunction} > Previous </button>

              <button onClick={applyColorByNode}> Next </button>
              <button onClick={initializeNetwork}> Reset </button>

        </div>
          </form>
          
          <div className='container'>
            <button onClick={solve_graph_coloring_STEP}> Full Graph Visualization</button>
          </div>

        </section>
      </div> 
    </div>
  );
};


export default GraphVisualizer;


// test
// 4
// (1, 2), (2, 3), (3, 4), (4,1)

// n = 9
// edge_list = (1, 5), (1, 7), (1, 9), (2, 4), (2, 5), (2, 9), (3, 4), (3, 6), (3,7), (3,8), (4, 5), (4,6), (4,7), (4,9),(5,6), (5,7),(6,8),(7,9),(8,9)